import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Users, Activity, Beaker, TrendingUp } from 'lucide-react';

interface UsageMetric {
  date: string;
  metric_type: string;
  metric_value: number;
  breakdown_by?: string;
  breakdown_value?: string;
}

interface ABExperiment {
  id: string;
  experiment_key: string;
  name: string;
  description: string;
  status: string;
  variants: Array<{ key: string; traffic: number }>;
  primary_metric: string;
  started_at?: string;
  ended_at?: string;
}

interface ConversionData {
  funnel_name: string;
  total_users: number;
  converted_users: number;
  conversion_rate: number;
}

export default function AnalyticsPage() {
  const [dauData, setDauData] = useState<UsageMetric[]>([]);
  const [experiments, setExperiments] = useState<ABExperiment[]>([]);
  const [dateRange, setDateRange] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load DAU metrics
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);

      const { data: dauData } = await supabase
        .from('usage_metrics')
        .select('*')
        .eq('metric_type', 'dau')
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (dauData) setDauData(dauData);

      // Load A/B experiments
      const { data: expData } = await supabase
        .from('ab_experiments')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (expData) setExperiments(expData);

      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const calculateDAU = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase.rpc('calculate_dau', {
        target_date: today,
      });

      if (error) throw error;
      alert(`DAU calculated: ${data} users`);
      await loadAnalytics();
    } catch (error) {
      console.error('Error calculating DAU:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">Loading analytics...</div>
      </div>
    );
  }

  const currentDAU = dauData[dauData.length - 1]?.metric_value || 0;
  const previousDAU = dauData[dauData.length - 2]?.metric_value || 0;
  const dauChange = previousDAU > 0 ? ((currentDAU - previousDAU) / previousDAU) * 100 : 0;

  return (
    <AdminPageLayout
      title="Analytics Dashboard"
      subtitle="Usage metrics and A/B testing"
      actions={
        <>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="px-4 py-2 border rounded-lg bg-white"
          >
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
          <button
            onClick={calculateDAU}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Calculate DAU
          </button>
        </>
      }
    >
      {/* Key Metrics */}
      <StatsGrid cols={4}>
        <StatCard
          label="Daily Active Users"
          value={currentDAU}
          icon={Users}
          subtitle={`${dauChange >= 0 ? '↑' : '↓'} ${Math.abs(dauChange).toFixed(1)}%`}
        />
        <StatCard
          label="Total Events (30d)"
          value={dauData.reduce((sum, d) => sum + d.metric_value, 0)}
          icon={Activity}
        />
        <StatCard
          label="Active Experiments"
          value={experiments.filter((e) => e.status === 'running').length}
          icon={Beaker}
        />
        <StatCard
          label="Avg DAU"
          value={Math.round(dauData.length > 0 ? dauData.reduce((sum, d) => sum + d.metric_value, 0) / dauData.length : 0)}
          icon={TrendingUp}
        />
      </StatsGrid>

      {/* DAU Trend */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Daily Active Users Trend</h2>
        <div className="h-64">
          {dauData.length > 0 ? (
            <SimpleTrendChart data={dauData} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No DAU data available
            </div>
          )}
        </div>
      </Card>

      {/* A/B Experiments */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">A/B Experiments</h2>
        <div className="space-y-4">
          {experiments.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No experiments found</div>
          ) : (
            experiments.map((exp) => (
              <div key={exp.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold">{exp.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{exp.description}</div>
                    <div className="flex gap-4 mt-2 text-sm">
                      <span className="text-gray-500">
                        Primary Metric: <span className="font-medium">{exp.primary_metric}</span>
                      </span>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 text-sm rounded ${
                      exp.status === 'running'
                        ? 'bg-green-100 text-green-800'
                        : exp.status === 'draft'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {exp.status}
                  </span>
                </div>
                <div className="mt-4 flex gap-2">
                  {exp.variants.map((variant) => (
                    <div
                      key={variant.key}
                      className="px-3 py-2 bg-gray-100 rounded text-sm"
                    >
                      <span className="font-medium">{variant.key}</span>
                      <span className="text-gray-600 ml-2">{variant.traffic}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Recent Events */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Recent Events</h2>
        <RecentEventsList />
      </Card>
    </AdminPageLayout>
  );
}

function SimpleTrendChart({ data }: { data: UsageMetric[] }) {
  const maxValue = Math.max(...data.map((d) => d.metric_value));
  const width = 100;

  return (
    <div className="flex items-end justify-between h-full gap-1">
      {data.map((point, i) => {
        const height = (point.metric_value / maxValue) * 100;
        return (
          <div
            key={i}
            className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 cursor-pointer relative group"
            style={{ height: `${height}%` }}
            title={`${new Date(point.date).toLocaleDateString()}: ${point.metric_value}`}
          >
            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 whitespace-nowrap">
              {new Date(point.date).toLocaleDateString()}: {point.metric_value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RecentEventsList() {
  const [events, setEvents] = useState<any[]>([]);

  useEffect(() => {
    loadRecentEvents();
  }, []);

  const loadRecentEvents = async () => {
    const { data } = await supabase
      .from('analytics_events')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(10);

    if (data) setEvents(data);
  };

  return (
    <div className="space-y-2">
      {events.length === 0 ? (
        <div className="text-center text-gray-500 py-4">No recent events</div>
      ) : (
        events.map((event) => (
          <div key={event.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex-1">
              <span className="font-medium">{event.event_name}</span>
              {event.event_category && (
                <span className="ml-2 text-sm text-gray-600">({event.event_category})</span>
              )}
              {event.user_id && (
                <span className="ml-2 text-xs text-gray-500">{event.user_id}</span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {new Date(event.timestamp).toLocaleTimeString()}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
